// Register the custom element
if (!customElements.get('back-in-stock')) {
    class BackInStock extends HTMLElement {
      constructor() {
        super();

        // Cache elements
        this.triggerBtn = this.querySelector('[js-trigger-btn]');
        this.modal = this.querySelector('[js-modal]');
        this.form = this.querySelector('[js-form]');
        this.emailInput = this.querySelector('[js-email]');
        this.variantIdInput = this.querySelector('[js-variant-id]');
        this.submitBtn = this.querySelector('[js-submit-btn]');
        this.loadingSpinner = this.querySelector('[js-loading-spinner]');
        this.btnText = this.querySelector('[js-btn-text]');
        this.messageDiv = this.querySelector('[js-message]');
        this.heading = this.querySelector('[js-heading]');

        this.init();
      }

      init() {
        this.bindEvents();
      }

      bindEvents() {
        // Modal events
        if (this.triggerBtn) {
          this.triggerBtn.addEventListener('click', () => this.openModal());
        }

        if (this.modal) {
          this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal || e.target.closest('.js-close-modal')) {
              this.closeModal();
            }
          });
        }

        // Form events
        if (this.form) {
          this.form.addEventListener('submit', (e) => this.handleSubmit(e));
        }

        if (this.emailInput) {
          this.emailInput.addEventListener('input', (e) => this.validateEmail(e));
        }

        // Keyboard events
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.modal && !this.modal.classList.contains('invisible')) {
            this.closeModal();
          }
        });
      }

      // Modal functionality
      openModal() {
        if (this.modal) {
          this.modal.classList.remove('invisible');
          this.modal.focus();
          document.body.classList.add('overflow-hidden');
        }
      }

      closeModal() {
        if (this.modal) {
          this.modal.classList.add('invisible');
          document.body.classList.remove('overflow-hidden');
          this.resetForm();
        }
      }

      // Form functionality
      resetForm() {
        if (this.form) {
          this.form.reset();
        }
        if (this.messageDiv) {
          this.messageDiv.style.display = 'none';
        }
        this.resetFormState();
      }

      resetFormState() {
        if (this.submitBtn) {
          this.submitBtn.disabled = false;
        }
        if (this.loadingSpinner) {
          this.loadingSpinner.style.display = 'none';
        }
        if (this.btnText) {
          this.btnText.textContent = 'Notify Me When Available';
        }
      }

      setLoadingState() {
        if (this.submitBtn) {
          this.submitBtn.disabled = true;
        }
        if (this.loadingSpinner) {
          this.loadingSpinner.style.display = 'inline-block';
        }
        if (this.btnText) {
          this.btnText.textContent = 'Subscribing...';
        }
      }

      showMessage(text, type) {
        if (this.messageDiv) {
          this.messageDiv.textContent = text;
          this.messageDiv.className = `message ${type}`;
          this.messageDiv.style.display = 'block';

          if (type === 'success') {
            setTimeout(() => {
              this.messageDiv.style.display = 'none';
              this.closeModal();
            }, 3000);
          }
        }
      }

      // Validation
      isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
      }

      validateEmail(e) {
        const email = e.target.value.trim();
        if (email && !this.isValidEmail(email)) {
          e.target.style.borderColor = '#f44336';
        } else if (email) {
          e.target.style.borderColor = '#4caf50';
        } else {
          e.target.style.borderColor = '#e1e5e9';
        }
      }

      // Variant ID formatting
      formatVariantIdForKlaviyo(variantId) {
        if (variantId.includes('$shopify:::$default:::')) {
          return variantId;
        }
        return `$shopify:::$default:::${variantId}`;
      }

      // Form submission
      async handleSubmit(e) {
        e.preventDefault();

        const email = this.emailInput.value.trim();
        const rawVariantId = this.variantIdInput.value.trim();

        // Validation
        if (!this.isValidEmail(email)) {
          this.showMessage('Please enter a valid email address.', 'error');
          return;
        }

        if (!rawVariantId) {
          this.showMessage('Variant ID is required.', 'error');
          return;
        }

        const variantId = this.formatVariantIdForKlaviyo(rawVariantId);
        this.setLoadingState();

        try {
          const response = await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=VzZPU3', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: '2024-06-15',
            },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email: email,
                      },
                    },
                  },
                  channels: ['EMAIL'],
                },
                relationships: {
                  variant: {
                    data: {
                      type: 'catalog-variant',
                      id: variantId,
                    },
                  },
                },
              },
            }),
          });

          if (response.status === 201 || response.status === 202) {
            this.showMessage("Great! You'll be notified when this item is back in stock.", 'success');
            this.form.reset();
          } else {
            const errorData = await response.json().catch(() => ({}));
            let errorMessage = 'Something went wrong. Please try again.';

            if (errorData.errors && errorData.errors.length > 0) {
              errorMessage = errorData.errors[0].detail || errorMessage;
            }

            this.showMessage(errorMessage, 'error');
          }
        } catch (error) {
          console.error('Network error:', error);
          this.showMessage('Network error. Please check your connection and try again.', 'error');
        } finally {
          this.resetFormState();
        }
      }
    }

    customElements.define('back-in-stock', BackInStock);
  }